"use client"

// Import the fetchSuggestionsAsync and selectSuggestions from SuggestionsSlice
import { useNavigate } from "react-router-dom"
import "./jobSearch.css"
import img from "./upwork.png" // Ensure the image path is correct
import tata from "./tatam.png"
import figma from "./figma.png"
import tweet from "./tweetlogo.png"
import insta from "./instalogo.png"
import zomato from "./zomato.png"
import ey from "./ey1.png"
import swiggy from "./swiggy.png"
import apple from "./apple.png"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchSuggestionsAsync, selectSuggestions } from "./Suggestions/SuggestionsSlice"
import { fetchPublicCoreLocationsAsync, selectPublicCoreLocations } from "./FetchLocationSlice/FetchLocationSlice"
import DreamJob from "./DreamJob"

const JOBLIST_PERSIST_KEY = "jobListPersist"

export function JobSearch() {
  const logos = [img, tata, figma, tweet, insta, zomato, ey, swiggy, apple]

  const [inputValue, setInputValue] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedSuggestions, setSelectedSuggestions] = useState([])
  const [selectedLocations, setSelectedLocations] = useState([])
  const suggestionsRef = useRef(null)
  const [selectedExperience, setSelectedExperience] = useState("")

  // New: location search UI state
  const [locationQuery, setLocationQuery] = useState("")
  const [showLocationDropdown, setShowLocationDropdown] = useState(false)
  const locationDropdownRef = useRef(null)

  const navigate = useNavigate()
  const coreLocation = useSelector(selectPublicCoreLocations)
  const locLoading = useSelector((state) => state.publicCoreLocations?.loading)
  const suggestions = useSelector(selectSuggestions)
  const dispatch = useDispatch()

  const handleInputChange = (e) => {
    setInputValue(e.target.value)
    setShowSuggestions(true)

    if (e.target.value.trim()) {
      dispatch(fetchSuggestionsAsync({ keyword: e.target.value }))
    }
  }

  useEffect(() => {
    dispatch(fetchPublicCoreLocationsAsync())
  }, [dispatch])

  // Click outside keyword suggestions
  useEffect(() => {
    function handleClickOutside(event) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Click outside location dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setShowLocationDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSuggestionClick = (suggestion) => {
    if (!selectedSuggestions.some((item) => item.uuid === suggestion.uuid)) {
      setSelectedSuggestions([...selectedSuggestions, suggestion])
    }
    setInputValue("")
    setShowSuggestions(false)
  }

  // Add selected location
  const handleLocationPick = (locationItem) => {
    if (locationItem && !selectedLocations.some((item) => item.uuid === locationItem.uuid)) {
      setSelectedLocations([...selectedLocations, locationItem])
    }
    setLocationQuery("")
    setShowLocationDropdown(false)
  }

  // Debounced location search
  useEffect(() => {
    if (!showLocationDropdown) return
    const term = locationQuery.trim()
    const t = setTimeout(() => {
      dispatch(fetchPublicCoreLocationsAsync({ keyword: term }))
    }, 300)
    return () => clearTimeout(t)
  }, [locationQuery, showLocationDropdown, dispatch])

  const handleSearch = () => {
    // Categorize suggestions by type
    const jsUuids = selectedSuggestions
      .filter((item) => item.uuid?.startsWith("js"))
      .map((item) => item.uuid.replace(/^js_/, ""))

    const jtUuids = selectedSuggestions
      .filter((item) => item.uuid?.startsWith("jt"))
      .map((item) => item.uuid.replace(/^jt_/, ""))

    const cnUuids = selectedSuggestions
      .filter((item) => item.uuid?.startsWith("cn"))
      .map((item) => item.uuid.replace(/^cn_/, ""))

    // Extract only UUIDs from selectedLocations
    const locationUUIDs = selectedLocations.map((location) => location.uuid)
    // Map for display on /jobs
    const locationLabelMap = Object.fromEntries(selectedLocations.map((l) => [l.uuid, l.title]))

    // Clear any old persisted state so new search doesn't get overridden
    try {
      sessionStorage.removeItem(JOBLIST_PERSIST_KEY)
    } catch (e) { }

    // Navigate to /jobs with search parameters
    navigate("/jobs", {
      state: {
        fromJobSearch: true, // tell JobList to ignore stale persisted filters
        keyword: inputValue,
        selectedLocations: locationUUIDs,
        locationLabelMap, // UUID -> title
        selectedSuggestions,
        skills: jsUuids,
        titles: jtUuids,
        company: cnUuids,
        experience: selectedExperience,
      },
    })
  }

  // Filter out already-selected locations from the dropdown list
  const availableLocations = Array.isArray(coreLocation)
    ? coreLocation.filter((loc) => !selectedLocations.some((s) => s.uuid === loc.uuid))
    : []

  return (
    <div className="flex flex-col items-left justify-between w-full relative">
      <div className="bgCover rounded-lg w-full relative">
        <div className="sm:py-28 py-12 sm:px-1 ">
          <DreamJob />
          <div className="sm:max-w-6xl w-full mt-1 sm:mt-2 px-5 sm:px-12">
            <div className="border-0">
              {/* Main search row */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 px-2 sm:px-6">
                <div className="relative w-full sm:w-64 md:w-80">
                  <input
                    className="rounded border-gray-200 w-full  py-2"
                    type="text"
                    placeholder="Job Title / Keywords or company"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={() => setShowSuggestions(true)}
                  />

                  {showSuggestions && suggestions.length > 0 && (
                    <div
                      ref={suggestionsRef}
                      className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded mt-1 shadow-md z-10 max-h-60 overflow-y-auto"
                    >
                      {suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion.title}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Searchable Location Dropdown with Debounce */}
                <div className="relative w-full sm:w-48 shrink-0" ref={locationDropdownRef}>
                  <input
                    className="rounded p-2 border-gray-200 w-full"
                    type="text"
                    placeholder="Select Location"
                    value={locationQuery}
                    onChange={(e) => {
                      setLocationQuery(e.target.value)
                      setShowLocationDropdown(true)
                    }}
                    onFocus={() => {
                      setShowLocationDropdown(true)
                      dispatch(fetchPublicCoreLocationsAsync({ keyword: locationQuery.trim() }))
                    }}
                  />
                  {showLocationDropdown && (
                    <div className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded mt-1 shadow-md z-10 max-h-60 overflow-y-auto">
                      {locLoading ? (
                        <div className="px-4 py-2 text-gray-500">Loading...</div>
                      ) : availableLocations.length > 0 ? (
                        availableLocations.map((item) => (
                          <div
                            key={item.id || item.uuid}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleLocationPick(item)}
                          >
                            {item.title}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">No locations found</div>
                      )}
                    </div>
                  )}
                </div>

                <select
                  className="rounded p-2 border-gray-200 w-full sm:w-48 shrink-0"
                  value={selectedExperience}
                  onChange={(e) => setSelectedExperience(e.target.value)}
                >
                  <option value="">Experience (Years)</option>
                  {Array.from({ length: 30 }, (_, i) => i + 1).map((year) => (
                    <option key={year} value={year}>
                      {year} {year > 1 ? "Years" : "Year"}
                    </option>
                  ))}
                </select>

                <div className="w-full sm:w-auto flex justify-center">
                  <button className="buttonR w-full sm:w-48 px-4 py-2 shrink-0" onClick={handleSearch}>
                    Find Jobs
                  </button>
                </div>
              </div>

              {/* Selected Suggestions and Locations */}
              <div className="flex flex-wrap gap-2 mt-3 px-2">
                {selectedSuggestions.map((suggestion) => (
                  <div
                    key={suggestion.uuid}
                    className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm flex items-center"
                  >
                    {suggestion.title}
                    <span
                      className="ml-2 cursor-pointer text-gray-500 hover:text-gray-700"
                      onClick={() =>
                        setSelectedSuggestions(selectedSuggestions.filter((s) => s.uuid !== suggestion.uuid))
                      }
                    >
                      ×
                    </span>
                  </div>
                ))}

                {selectedLocations.map((location) => (
                  <div
                    key={location.uuid}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center"
                  >
                    {location.title}
                    <span
                      className="ml-2 cursor-pointer text-blue-500 hover:text-blue-700"
                      onClick={() => setSelectedLocations(selectedLocations.filter((l) => l.uuid !== location.uuid))}
                    >
                      ×
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Stats Section */}
        </div>
      </div>

      <div className="h-28 flex items-center justify-center w-full mainpageslider">
        <div className="slider-track flex w-full items-center">
          {logos.concat(logos).map((logo, index) => (
            <div key={`${logo}-${index}`} className="slider-item">
              <img src={logo || "/placeholder.svg"} alt={`Logo ${index + 1}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}